import React, { useMemo } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Popover } from 'antd';
import { ToolOutlined } from '@ant-design/icons';

import StepPopupUnassign from './StepPopupUnassign';
import { STEP_TYPE } from '../../constants';
import { stopMatchesFilter } from '../../utils/stopFilter';

// SERVICE stops get their own look (dashed purple + tool glyph) so the
// operator can tell a single-visit service task from a pickup/delivery leg
// at a glance (Diego, Aug 18 2026).
const SERVICE_COLOR = '#722ed1';

const UnScheduledSteps = ({
  steps,
  realSteps,
  stepsHovered,
  setStepsHovered,
  onClickStep,
  handleCloseStepPopup,
  setting,
  selectedMultipleSteps,
  multipleStepState,
  selectedStep,
  stopFilter,
}) => {
  const newClickStep = (e, step) => {
    if ((e.ctrlKey || e.metaKey || e.shiftKey) && selectedMultipleSteps) {
      selectedMultipleSteps && selectedMultipleSteps(step, 'unscheduled');
    } else {
      onClickStep(step);
    }
  };

  // Order codes that pass the day-plan filter — the SAME matcher as the map
  // pins + the optimize set (this used to be a private regex copy that drifted
  // from OVO-149/150/205 and threw on a "(" in the query). Order-level OR: if
  // any leg matches, both legs of that order stay at full opacity.
  const filteredSteps = useMemo(() => {
    return steps.filter((step) => stopMatchesFilter(step, stopFilter)).map((step) => step.orderCode);
  }, [steps, stopFilter]);
  return (
    <>
      {steps.map((step) => {
        // The drag index is the position of the order's DELIVERY pseudo-stop
        // in realSteps (what moveStep reads back). A SERVICE order has ONE
        // pseudo-stop of type SERVICE (Aug 18) — matching DELIVERY only gave
        // index -1, and dropping it on a route crashed moveStep (#128).
        const stepOrderIds = step.orderIds || (step.orderId ? [step.orderId] : []);
        const keyIndex = realSteps.findIndex((rs) => {
          const rsOrderIds = rs.orderIds || (rs.orderId ? [rs.orderId] : []);
          return (
            (rs.type === STEP_TYPE.delivery || rs.type === STEP_TYPE.service) &&
            stepOrderIds.some((orderId) => rsOrderIds.includes(orderId))
          );
        });
        const keyDraggable = `draggable${step.id}${keyIndex}`;
        const stepPopover = `StepPopover-${stepOrderIds[0] || step.id}-${step.type.toLowerCase()}`;
        const isMultipleSelected = multipleStepState?.steps?.find((item) => {
          const itemOrderIds = item.orderIds || (item.orderId ? [item.orderId] : []);
          return (
            stepOrderIds.some((orderId) => itemOrderIds.includes(orderId)) &&
            item.type === step.type &&
            item.driverId === 'unscheduled'
          );
        });
        return (
          <Draggable key={keyDraggable} draggableId={keyDraggable} index={keyIndex}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                  zIndex: 99,
                  width: 24,
                  marginLeft: '8px',
                  ...provided.draggableProps.style,
                }}
              >
                <Popover
                  key={keyIndex}
                  trigger="hover"
                  open={stepsHovered[stepPopover] || false}
                  content={
                    <StepPopupUnassign
                      step={step}
                      stepPopover={stepPopover}
                      setStepsHovered={setStepsHovered}
                      onClickStep={onClickStep}
                      setting={setting}
                    />
                  }
                >
                  <div
                    className={`${
                      step.type.toLowerCase() === 'pickup'
                        ? 'unit'
                        : step.type.toLowerCase() === 'service'
                          ? 'unit unit2 unit-service'
                          : 'unit unit2'
                    } ${stepPopover}`}
                    style={{
                      borderColor: step.type.toLowerCase() === 'service' ? SERVICE_COLOR : step.color,
                      color: step.type.toLowerCase() === 'service' ? SERVICE_COLOR : step.color,
                      borderWidth: isMultipleSelected || selectedStep?.id === step.id ? '3px' : '1px',
                      opacity: filteredSteps.includes(step.orderCode) ? 1 : 0.3,
                    }}
                    onClick={(e) => newClickStep(e, step)}
                    onMouseOut={() => handleCloseStepPopup()}
                  >
                    {step.index}
                    {step.type.toLowerCase() === 'service' && <ToolOutlined />}
                  </div>
                </Popover>
              </div>
            )}
          </Draggable>
        );
      })}
    </>
  );
};

export default UnScheduledSteps;
